import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/material';
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const { t } = useTranslation();

  const location = useLocation();

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  // ✅ App.js navigate state -> Profile (refresh olursa sessionStorage fallback)
  const accessReason = useMemo(() => {
    const fromState = location.state?.accessReason;
    const fromSession = sessionStorage.getItem('accessReason');

    const reason = fromState || fromSession || null;

    if (fromState) {
      sessionStorage.setItem('accessReason', fromState);
    }

    return reason;
  }, [location.state]);

  const clearAccessReason = () => {
    sessionStorage.removeItem('accessReason');
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');

        // App.js zaten token yoksa login'e atıyor ama burada da güvenli olsun
        if (!token) return;

        // ✅ App.js ile aynı endpoint
        const response = await api.get('/api/users/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });

        setProfile(response.data);
        setName(response.data.name);
      } catch (error) {
        console.error('Error fetching profile:', error.message);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveName = async () => {
    if (!name.trim()) {
      setMessage(t("name_field_empty"));
      return;
    }

    setIsSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await api.put(
        '/api/users/profile',
        { name },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setProfile((prev) => ({ ...prev, name: response.data.name }));
      setName(response.data.name);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating name:', error.message);
      setMessage(t("name_update_failed"));
    } finally {
      setIsSaving(false);
    }
  };

  const isTrialActive = () => {
    if (!profile?.free_trial_end_date) return false;
    const currentDate = new Date();
    const trialEndDate = new Date(profile.free_trial_end_date);
    return trialEndDate > currentDate;
  };

  const trialDaysLeft = () => {
    if (!profile?.free_trial_end_date) return 0;
    const currentDate = new Date();
    const trialEndDate = new Date(profile.free_trial_end_date);
    const difference = Math.ceil((trialEndDate - currentDate) / (1000 * 60 * 60 * 24));
    return difference > 0 ? difference : 0;
  };

  const openMobileAppLanding = () => {
    // Şimdilik: mobil yönlendirme sayfan / store linklerin olabilir
    // İstersen bunu daha sonra platforma göre dinamik yaparız.
    window.location.href = "https://www.ganttmaster.com";
  };

  if (!profile) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Container maxWidth="sm">
      <Box mt={5} display="flex" justifyContent="center">
        <Card sx={{ width: '100%', maxWidth: 600, borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
              {t("profile.title")}
            </Typography>

            {/* ✅ Access Reason Banner */}
            {accessReason && (
              <Alert
                severity="warning"
                sx={{ mb: 2, borderRadius: 2 }}
                onClose={clearAccessReason}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t("access.title")}
                </Typography>

                <Typography sx={{ mt: 1 }}>
                  {t(`access.${accessReason}`)}
                </Typography>

                <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                  {accessReason === "manager_inactive" && (
                    <Button variant="contained" onClick={openMobileAppLanding}>
                      {t("access.renew_cta")}
                    </Button>
                  )}

                  {(accessReason === "premium_required" || accessReason === "no_projects") && (
                    <Button variant="contained" onClick={openMobileAppLanding}>
                      {t("access.download_cta")}
                    </Button>
                  )}

                  <Button variant="outlined" onClick={clearAccessReason}>
                    {t("access.close")}
                  </Button>
                </Box>
              </Alert>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Name */}
            <Box mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t("profile.name")}:
              </Typography>

              {isEditing ? (
                <Box>
                  <TextField
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    variant="outlined"
                    disabled={isSaving}
                    sx={{ mt: 1 }}
                  />
                  <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveName}
                      disabled={isSaving}
                    >
                      {isSaving ? t("profile.saving") : t("profile.save")}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setIsEditing(false);
                        setName(profile.name);
                      }}
                    >
                      {t("profile.cancel")}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                  <Typography>{profile.name}</Typography>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setIsEditing(true)}
                  >
                    {t("profile.edit")}
                  </Button>
                </Box>
              )}

              {!!message && (
                <Typography sx={{ mt: 1 }} color="error">
                  {message}
                </Typography>
              )}
            </Box>

            {/* Email */}
            <Box mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t("profile.email")}:
              </Typography>
              <Typography>{profile.email}</Typography>
            </Box>

            {/* Subscription */}
            <Box mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t("profile.subscription_status")}:
              </Typography>

              {profile.lifetimeAccess ? (
                <Typography>{t("profile.free_access")}</Typography>
              ) : profile.subscription_status === "active" || profile.isSubscribed ? (
                <Typography>{t("profile.subscribed_active")}</Typography>
              ) : isTrialActive() ? (
                <Typography>
                  {t("profile.trial_active", { days: trialDaysLeft() })}
                </Typography>
              ) : (
                <Typography>{t("profile.not_subscribed")}</Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Profile;
